import AddIcon from '@mui/icons-material/Add';
import {
  Avatar,
  Box,
  Card,
  CardHeader,
  Chip,
  IconButton,
  ListItem,
  ListItemProps,
  Typography,
} from '@mui/material';
import { B2BItem } from 'api/public/types';
import { ReactComponent as TagIcon } from 'assets/icons/Tag.svg';
import placeholderThumbnailImg from 'assets/images/placeholder-thumbnail.png';
import { ElementType, FC, forwardRef, MouseEvent } from 'react';
import { isMobile } from 'react-device-detect';
import { createSearchParams, useNavigate } from 'react-router-dom';
import LinkWithQuery from 'shared/components/LinkWithQuery/LinkWithQuery';
import {
  formatPriceRangeDisplay,
  generateLetterAvatar,
  highlightSearchTextFromString,
} from 'shared/utils/common';

type CategoryListItemProps = ListItemProps & {
  item: B2BItem;
  showInstantAddButton?: boolean;
  markValue?: string;
  direction?: 'horizontal' | 'vertical';
  component?: ElementType<any>;
};

const CategoryListItem: FC<CategoryListItemProps> = forwardRef(
  (
    {
      item,
      showInstantAddButton = false,
      markValue,
      direction = 'horizontal',
      component = 'li',
      className,
      ...props
    },
    ref
  ) => {
    const navigate = useNavigate();
    const handleAddItem = (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      navigate({
        pathname: `/${item.category[0]}/${item.slug}`,
        search: `?${createSearchParams({ show: 'true' })}`,
      });
    };

    const hasDiscount =
      item.models && item.models.some((model) => model.original_price);

    return (
      <ListItem
        ref={ref}
        className={`!items-start group${
          direction === 'horizontal' ? ' hover:bg-gray-50' : ''
        } ${className}`}
        component={component}
        {...props}
      >
        <Card
          component={LinkWithQuery}
          to={`/${item.category && item.category[0]}/${item.slug}`}
          title={`${item.title_vi_vn} - ${formatPriceRangeDisplay(
            'price',
            item.models,
            item.unit
          )}`}
          elevation={0}
          sx={{
            display: 'block',
            backgroundColor: 'transparent',
            width: '100%',
            borderRadius: 0,
          }}
        >
          <CardHeader
            className={direction === 'vertical' ? 'space-y-2.5' : ''}
            sx={{
              padding: 0,
              alignItems: 'flex-start',
              width: '100%',
              // minHeight: 144,
              flexDirection: direction === 'vertical' ? 'column' : 'row',
            }}
            avatar={
              <div className='relative'>
                {hasDiscount && (
                  <Chip
                    size='small'
                    className='shadow'
                    label='Promo'
                    sx={{
                      height: 18,
                      backgroundColor: 'secondary.dark',
                      color: 'white',
                      fontWeight: 700,
                      borderRadius: 0,
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      zIndex: 10,
                      span: {
                        paddingX: 0.75,
                      },
                      ':after': {
                        content: '""',
                        width: 0,
                        height: 0,
                        borderTop: '9px solid #096DD9',
                        borderBottom: '9px solid #096DD9',
                        borderRight: '6px solid transparent',
                        position: 'absolute',
                        top: 0,
                        right: -6,
                        bottom: 0,
                      },
                    }}
                  />
                )}
                <Box className='relative overflow-hidden rounded'>
                  <Avatar
                    sx={{
                      width:
                        direction === 'vertical' ? 150 : isMobile ? 100 : 120,
                      height:
                        direction === 'vertical' ? 150 : isMobile ? 100 : 120,
                      borderRadius: 1,
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: 'divider',
                    }}
                    alt={item.title_vi_vn}
                    src={item.item_img_url || placeholderThumbnailImg}
                  >
                    {generateLetterAvatar(item.title_vi_vn)}
                  </Avatar>
                  {item.is_sold_out && (
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                      position='absolute'
                      top={0}
                      left={0}
                      className='h-full w-full bg-black bg-opacity-25 p-1'
                    >
                      <Typography
                        variant='caption'
                        fontWeight={700}
                        className='w-full max-w-[72px] truncate rounded-[45px] bg-[#121619] bg-opacity-70 p-2 text-white'
                      >
                        Hết hàng
                      </Typography>
                    </Box>
                  )}
                </Box>
              </div>
            }
            title={
              <>
                <Typography
                  variant='subtitle2'
                  className='line-clamp-2 group-hover:underline'
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: markValue
                        ? highlightSearchTextFromString(
                            markValue,
                            item.title_vi_vn
                          )
                        : item.title_vi_vn,
                    }}
                  ></span>
                </Typography>
                {direction === 'horizontal' && (
                  <Typography
                    variant='caption'
                    className='line-clamp-1'
                    color='grey.500'
                  >
                    {item.sub_title_vi_vn}
                  </Typography>
                )}
                {hasDiscount && (
                  <Box className='flex flex-row items-center space-x-1'>
                    {/* <TagIcon className='h-3.5 w-3.5' />
                  <Typography
                    variant='caption'
                    className='line-clamp-1'
                    color='secondary.dark'
                    fontWeight={700}
                  >
                    -45%
                  </Typography> */}

                    <Typography
                      variant='body2'
                      color='grey.400'
                      sx={{ textDecoration: 'line-through' }}
                    >
                      {formatPriceRangeDisplay(
                        'original_price',
                        item.models,
                        item.unit
                      )}
                    </Typography>
                  </Box>
                )}
              </>
            }
            titleTypographyProps={{
              component: 'div',
              className: 'space-y-1',
            }}
            subheader={
              <>
                <Box
                  display='flex'
                  justifyContent='space-between'
                  alignItems='center'
                  marginTop={0.5}
                >
                  <Typography variant='body1' fontWeight={700} color='primary'>
                    {formatPriceRangeDisplay('price', item.models, item.unit)}
                  </Typography>
                  {showInstantAddButton && (
                    <IconButton
                      size='small'
                      color='primary'
                      onClick={handleAddItem}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </Box>

                {direction === 'horizontal' && item.promo && (
                  <Box className='inline-flex flex-row flex-nowrap items-center space-x-1 border-t border-[#F0F4F9] pt-1.5 pr-1'>
                    <TagIcon className='h-3.5 w-3.5 flex-none' />
                    <Typography
                      variant='body2'
                      color='grey.900'
                      className='line-clamp-1 grow'
                    >
                      {item.promo}
                    </Typography>
                  </Box>
                )}
                {/* {item.is_sold_out && (
          <Alert
            sx={{ marginTop: 1.25, paddingY: 0, paddingX: 1 }}
            severity='warning'
            color='warning'
            icon={
              <SvgIcon
                sx={{ fontSize: 20 }}
                component={NotificationIcon}
                inheritViewBox
              />
            }
            action={
              <IconButton
                component='span'
                size='small'
                aria-label='voucher'
                sx={{ color: 'grey.400' }}
              >
                <ChevronRightIcon />
              </IconButton>
            }
          >
            Nhắc tôi khi có sản phẩm này
          </Alert>
        )} */}
              </>
            }
            subheaderTypographyProps={{
              component: 'div',
              className: 'space-y-2',
            }}
          />
        </Card>
      </ListItem>
    );
  }
);

export default CategoryListItem;
